import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Copy, MessageCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MCP_ENDPOINT = 'https://v2.mundpay.com/mcp';

type McpClientId = 'cursor' | 'claudecode' | 'claudedesktop' | 'vscode' | 'other';

type McpClient = {
  id: McpClientId;
  label: string;
  filename: string;
  logoBg: string;
  steps: string[];
  code: string;
};

function copyText(value: string) {
  return navigator.clipboard.writeText(value);
}

export function DevelopersMcpPanel() {
  const { t } = useTranslation('integration');
  const [activeClient, setActiveClient] = useState<McpClientId>('cursor');
  const [copied, setCopied] = useState<'endpoint' | 'config' | null>(null);

  const clients = useMemo<McpClient[]>(
    () => [
      {
        id: 'cursor',
        label: 'Cursor',
        filename: '~/.cursor/mcp.json',
        logoBg: '#000000',
        steps: [
          t('developers.mcp.cursor_step_1'),
          t('developers.mcp.cursor_step_2'),
          t('developers.mcp.cursor_step_3'),
          t('developers.mcp.cursor_step_4'),
          t('developers.mcp.cursor_step_5'),
        ],
        code: `{\n    "mcpServers": {\n        "mundpay": {\n            "url": "${MCP_ENDPOINT}"\n        }\n    }\n}`,
      },
      {
        id: 'claudecode',
        label: 'Claude Code',
        filename: t('developers.mcp.claude_code_file'),
        logoBg: '#D97757',
        steps: [
          t('developers.mcp.claude_code_step_1'),
          t('developers.mcp.claude_code_step_2'),
        ],
        code: `claude mcp add --transport http mundpay ${MCP_ENDPOINT}`,
      },
      {
        id: 'claudedesktop',
        label: 'Claude Desktop',
        filename: 'claude_desktop_config.json',
        logoBg: '#D97757',
        steps: [
          t('developers.mcp.claude_desktop_step_1'),
          t('developers.mcp.claude_desktop_step_2'),
          t('developers.mcp.claude_desktop_step_3'),
          t('developers.mcp.claude_desktop_step_4'),
        ],
        code: `{\n    "mcpServers": {\n        "mundpay": {\n            "command": "npx",\n            "args": [\n                "-y",\n                "mcp-remote",\n                "${MCP_ENDPOINT}"\n            ]\n        }\n    }\n}`,
      },
      {
        id: 'vscode',
        label: 'VS Code',
        filename: '.vscode/mcp.json',
        logoBg: '#007ACC',
        steps: [
          t('developers.mcp.vscode_step_1'),
          t('developers.mcp.vscode_step_2'),
        ],
        code: `{\n    "servers": {\n        "mundpay": {\n            "type": "http",\n            "url": "${MCP_ENDPOINT}"\n        }\n    }\n}`,
      },
      {
        id: 'other',
        label: t('developers.mcp.other_label'),
        filename: t('developers.mcp.other_file'),
        logoBg: '#3F3F46',
        steps: [t('developers.mcp.other_step_1')],
        code: MCP_ENDPOINT,
      },
    ],
    [t]
  );

  const current = clients.find((client) => client.id === activeClient) ?? clients[0];

  const handleCopy = async (value: string, kind: 'endpoint' | 'config') => {
    try {
      await copyText(value);
      setCopied(kind);
      toast({
        description:
          kind === 'endpoint'
            ? t('developers.mcp.endpoint_copied')
            : t('developers.mcp.config_copied'),
        variant: 'success',
      });
      window.setTimeout(() => setCopied(null), 1800);
    } catch {
      toast({
        description: t('developers.copy_error'),
        variant: 'destructive',
      });
    }
  };

  const prompts = [
    t('developers.mcp.prompt_1'),
    t('developers.mcp.prompt_2'),
    t('developers.mcp.prompt_3'),
    t('developers.mcp.prompt_4'),
  ];

  return (
    <motion.div
      className='space-y-4'
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className='p-5'>
        <div className='flex items-start gap-3'>
          <div className='flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary'>
            <MessageCircle className='size-4' />
          </div>
          <div className='min-w-0 flex-1'>
            <strong className='block text-[14.5px] font-semibold'>
              {t('developers.mcp.title')}
            </strong>
            <p className='mt-1 text-[13px] leading-6 text-muted'>
              {t('developers.mcp.description')}
            </p>
            <div className='mt-3 flex flex-wrap items-center gap-2'>
              <span className='rounded-full bg-white/5 px-2.5 py-1 font-mono text-xs font-medium text-muted'>
                {MCP_ENDPOINT}
              </span>
              <button
                type='button'
                className='flex size-7 items-center justify-center rounded-md bg-white/5 text-muted hover:text-white'
                onClick={() => handleCopy(MCP_ENDPOINT, 'endpoint')}
                title={t('developers.copy')}
              >
                {copied === 'endpoint' ? (
                  <Check className='size-3.5 text-primary' />
                ) : (
                  <Copy className='size-3.5' />
                )}
              </button>
              <span className='inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-white'>
                <i className='block size-1.5 rounded-full bg-primary' />
                {t('developers.mcp.no_auth')}
              </span>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className='px-5 py-4'>
          <h2 className='text-[14.5px] font-semibold'>
            {t('developers.mcp.connect_title')}
          </h2>
          <p className='mt-0.5 text-xs text-muted'>
            {t('developers.mcp.connect_subtitle')}
          </p>
        </div>
        <div className='flex flex-wrap items-center justify-start gap-2 px-5 pb-5'>
          {clients.map((client) => (
            <button
              key={client.id}
              type='button'
              onClick={() => setActiveClient(client.id)}
              className={cn(
                'inline-flex items-center gap-2 rounded-full border border-colorBorder bg-[#18181b] px-3 py-1.5 text-left',
                activeClient === client.id && 'border-primary/50 bg-primary/10'
              )}
            >
              <span
                className='flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white'
                style={{ background: client.logoBg }}
              >
                {client.label.slice(0, 2).toUpperCase()}
              </span>
              <span
                className={cn(
                  'text-xs font-medium text-muted',
                  activeClient === client.id && 'text-white'
                )}
              >
                {client.label}
              </span>
            </button>
          ))}
        </div>
        <div className='px-5 pb-5'>
          <AnimatePresence mode='wait'>
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              <ol className='mb-3.5 list-decimal space-y-1 pl-5 text-[13px] leading-7 text-muted'>
                {current.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              <div className='overflow-hidden rounded-lg border border-colorBorder'>
                <div className='flex items-center justify-between bg-[#141416] px-4 py-2.5'>
                  <span className='font-mono text-[11.5px] text-muted'>
                    {current.filename}
                  </span>
                  <Button
                    type='button'
                    variant='secondary'
                    size='sm'
                    className='h-8 rounded-full'
                    onClick={() => handleCopy(current.code, 'config')}
                  >
                    {copied === 'config' ? <Check /> : <Copy />}
                    {t('developers.copy')}
                  </Button>
                </div>
                <pre className='overflow-x-auto p-5 font-mono text-[12.5px] leading-7 text-muted'>
                  {current.code}
                </pre>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </Card>

      <Card className='p-5'>
        <h2 className='mb-3 text-[14.5px] font-semibold'>
          {t('developers.mcp.prompts_title')}
        </h2>
        <ul className='flex flex-col gap-2'>
          {prompts.map((prompt) => (
            <li
              key={prompt}
              className='flex items-center gap-2.5 rounded-md border border-colorBorder bg-[#09090b] px-3 py-2.5 text-[13px] text-muted'
            >
              <MessageCircle className='size-3.5 shrink-0' />
              {prompt}
            </li>
          ))}
        </ul>
        <p className='mt-3.5 text-xs text-muted'>{t('developers.mcp.footer')}</p>
      </Card>
    </motion.div>
  );
}
